import _, { isArray } from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { RefreshCw } from 'lucide-react';
import Select from 'src/components/ui/BrandSelect';
import { AutoSizer, Column, Table } from 'react-virtualized';
import 'react-virtualized/styles.css';
import { Actions as ADMINACTIONS } from '../../../store/actions/adminActions';
import { Actions as ORGANISATIONACTIONS } from '../../../store/actions/organisationActions';
import { Button } from '../../ui/button';
import { itemToOptions } from '../../utils/GlobalFunctions';
import { adminPortalSelect } from '../../utils/ReactSelectStyles';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Spinner } from '../../ui/spinner';
import { billingRelationshipLabel, resolveClientStatus } from '../../utils/consultantClientUtils';
import { Alert, AlertDescription } from '../../ui/alert';
import { isWhiteLabelFirmOrg } from '../../utils/managementShell';

const OrganisationListTable = ({
  sortedOrganisations,
  tableName,
  getAllItemsFromDB,
  branding,
  industryOptions,
  setCurrentOrganisation,
  countries,
  toggleModal,
  getWLAdminOrgs,
  cyfAdmin,
  WhiteLabelAdmin,
  consultantAdmin,
  members,
  statusOptions,
  isWhiteLabelApp,
  whiteLabelAdminOrgs,
  consultantAdminOrgs,
  accountTypeOpt,
  currencies,
}) => {
  const history = useHistory();

  const { headers } = branding.fonts?.[0].font || {};
  const { primaryLinkText } = branding.colors || {};

  const [data, setData] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterByWhiteLabel, setFilterByWhiteLabel] = useState('');
  const [filterByConsultant, setFilterByConsultant] = useState('');
  const [filterByAccountType, setFilterByAccountType] = useState('');
  const [filterByAccountLevel, setFilterByAccountLevel] = useState('');
  const [filterBySubscription, setFilterBySubscription] = useState('');

  const isParentConsultant = (org) => org?.consultantId === org?.id;
  const isParentWhiteLabel = (org) => org?.whiteLabelId === org?.id;
  const isParentAccount = (org) => isParentConsultant(org) || isParentWhiteLabel(org);

  const accountLevelOptions = [
    { label: 'Parent consultants', value: 'parentConsultant' },
    { label: 'Parent white labels', value: 'parentWhiteLabel' },
    { label: 'All parent accounts', value: 'allParents' },
  ];

  useEffect(() => {
    if (!sortedOrganisations) getAllItemsFromDB(tableName);
    if (!members) getAllItemsFromDB('profile');
    if (!whiteLabelAdminOrgs) getWLAdminOrgs();
    if (!currencies) getAllItemsFromDB('currencies');
  }, [getAllItemsFromDB, tableName, sortedOrganisations, members, getWLAdminOrgs, whiteLabelAdminOrgs, currencies]);

  const filteringByName = () => {
    const filterOrgs =
      isArray(sortedOrganisations) &&
      sortedOrganisations?.filter(
        (org) =>
          (!filterName ||
            org.id?.toLowerCase().trim().includes(filterName?.toLowerCase().trim()) ||
            org.name?.toLowerCase().trim().includes(filterName?.toLowerCase().trim()) ||
            org.accountType?.toLowerCase().trim().includes(filterName?.toLowerCase().trim()) ||
            org.vatNumber?.toLowerCase().trim().includes(filterName?.toLowerCase().trim()) ||
            org.leiCode?.toLowerCase().trim().includes(filterName?.toLowerCase().trim()) ||
            org.isinNumber?.toLowerCase().trim().includes(filterName?.toLowerCase().trim()) ||
            org.countryName?.toLowerCase().trim().includes(filterName?.toLowerCase().trim())) &&
          (!filterIndustry || org.industry === filterIndustry) &&
          (!filterByWhiteLabel || org.whiteLabelId === filterByWhiteLabel) &&
          (!filterByConsultant || org.consultantId === filterByConsultant) &&
          (!filterByAccountType || org.accountType === filterByAccountType) &&
          (!filterByAccountLevel ||
            (filterByAccountLevel === 'parentConsultant' && isParentConsultant(org)) ||
            (filterByAccountLevel === 'parentWhiteLabel' && isParentWhiteLabel(org)) ||
            (filterByAccountLevel === 'allParents' && isParentAccount(org))) &&
          (!filterBySubscription ||
          (filterBySubscription === 'active' &&
              (!org.subscriptionEndDate || (org.subscriptionEndDate && new Date() <= new Date(new Date(org.subscriptionEndDate).setDate(new Date(org.subscriptionEndDate).getDate() + 30))))) ||
          (filterBySubscription === 'grace' &&
            org.subscriptionEndDate &&
            new Date(org.subscriptionEndDate) < new Date() &&
            new Date() <= new Date(new Date(org.subscriptionEndDate).setDate(new Date(org.subscriptionEndDate).getDate() + 30))) ||
          (filterBySubscription === 'ended' && org.subscriptionEndDate && new Date(org.subscriptionEndDate) < new Date()))
      );
    setData(filterOrgs);
  };

  useEffect(() => {
    filteringByName();
    // eslint-disable-next-line
  }, [
    sortedOrganisations,
    filterName,
    filterIndustry,
    filterByWhiteLabel,
    filterByConsultant,
    filterByAccountType,
    filterByAccountLevel,
    filterBySubscription,
  ]);

  if (_.isString(sortedOrganisations)) {
    return (
      <div>
        <h1>No Clients</h1>
      </div>
    );
  }

  const updateOrganisation = (organisation) => {
    setCurrentOrganisation(organisation);
    localStorage.setItem('Organisation', organisation.id);
  };

  const getArray = (dataKey) => {
    let arr;
    switch (dataKey) {
      case 'industry':
        arr = industryOptions;
        break;
      case 'country':
        arr = countries;
        break;
      case 'userId':
        arr = members;
        break;
      case 'whiteLabelId':
        arr = whiteLabelAdminOrgs;
        break;
      case 'consultantId':
        arr = consultantAdminOrgs;
        break;
      case 'parentOrganisation':
        arr = sortedOrganisations;
        break;
      case 'active':
        arr = statusOptions;
        break;
      default:
        break;
    }
    return arr;
  };

  const formatCellValue = ({ cellData, dataKey, rowData }) => {
    if (dataKey === 'parentOrganisation') {
      const value = cellData && cellData !== 'true' ? _.find(sortedOrganisations, { id: cellData })?.name : 'Yes';
      return (
        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className='cursor-pointer admin_portal_cellData_description'>{value}</div>
            </TooltipTrigger>
            <TooltipContent>{value}</TooltipContent>
          </Tooltip>
        </div>
      );
    } else if (dataKey === 'reportingYearEndDay') {
      const { reportingYearEndDay, reportingYearEndMonth } = rowData;
      return (
        <div className='cursor-pointer admin_portal_cellData_description'>
          {reportingYearEndDay && reportingYearEndMonth ? `${reportingYearEndDay}/${reportingYearEndMonth} ` : ''}
        </div>
      );
    } else if (dataKey === 'country') {
      const value = cellData ? _.find(countries, { id: cellData })?.name : '';
      return (
        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className='cursor-pointer admin_portal_cellData_description'>{value}</div>
            </TooltipTrigger>
            <TooltipContent>{value}</TooltipContent>
          </Tooltip>
        </div>
      );
    } else if (dataKey === 'currency') {
      const value = cellData ? _.find(currencies, { id: cellData })?.isoCode : '';
      return (
        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className='cursor-pointer admin_portal_cellData_description'>{value}</div>
            </TooltipTrigger>
            <TooltipContent>{value}</TooltipContent>
          </Tooltip>
        </div>
      );
    } else if (dataKey === 'subscriptionEndDate') {
      const value = cellData ? new Date(cellData).toLocaleDateString() : 'Indefinite';
      return (
        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className='cursor-pointer admin_portal_cellData_description'>{value}</div>
            </TooltipTrigger>
            <TooltipContent>{value}</TooltipContent>
          </Tooltip>
        </div>
      );
    } else {
      const arr = getArray(dataKey);
      return (
        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className='cursor-pointer admin_portal_cellData_description'>
                {cellData && arr ? _.find(arr, { value: cellData })?.label : cellData}
              </div>
            </TooltipTrigger>
            <TooltipContent>{cellData && arr ? _.find(arr, { value: cellData })?.label : cellData}</TooltipContent>
          </Tooltip>
        </div>
      );
    }
  };

  const reportingPeriod = ({ cellData, rowData }) => {
    return cellData === null || cellData === undefined
      ? 'N/A'
      : `${rowData.reportingYearEndDay}/${rowData.reportingYearEndMonth}`;
  };

  const action = ({ rowData, dataKey }) => {
    if (['facility', 'report', 'usage'].includes(dataKey)) {
      const path = dataKey === 'report' ? 'reports' : dataKey === 'usage' ? 'usage' : 'facilities';
      return (
        <a
          className='cursor-pointer text-primary underline'
          onClick={() => {
            updateOrganisation(rowData);
            history.push(`/${path}`);
          }}>
          {path.charAt(0).toUpperCase() + path.slice(1)}
        </a>
      );
    } else if (['reportGeneration'].includes(dataKey)) {
      const textField = rowData[dataKey] ? 'Disable Reports' : 'Enable Reports';
      const showOnlyAdminOrgs = [rowData.whiteLabelId, rowData.consultantId].includes(rowData.id);
      if (showOnlyAdminOrgs && !cyfAdmin) return null;
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Switch
                checked={!rowData[dataKey]}
                onCheckedChange={() => {
                  if (rowData[dataKey]) {
                    toggleModal(true, 'disableOrgReports-table-disable', { ...rowData, reportGeneration: false });
                  } else {
                    toggleModal(true, 'enableOrgReports-table-disable', { ...rowData, reportGeneration: true });
                  }
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>{textField}</TooltipContent>
        </Tooltip>
      );
    } else if (['organisationCreation'].includes(dataKey)) {
      const textField = rowData[dataKey] ? 'Disable Add Organisation' : 'Enable Add Organisation';
      const showOnlyAdminOrgs = [rowData.whiteLabelId, rowData.consultantId].includes(rowData.id);
      if (showOnlyAdminOrgs && !cyfAdmin) return null;
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Switch
                checked={!rowData[dataKey]}
                onCheckedChange={() => {
                  if (rowData[dataKey]) {
                    toggleModal(true, 'disableOrgCreation-table-disable', { ...rowData, organisationCreation: false });
                  } else {
                    toggleModal(true, 'enableOrgCreation-table-disable', { ...rowData, organisationCreation: true });
                  }
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>{textField}</TooltipContent>
        </Tooltip>
      );
    } else if (['excludeFromDeletion'].includes(dataKey)) {
      const textField = !rowData[dataKey] ? 'Exclude Organisation' : 'Include Organisation';
      if (rowData?.accountType?.includes('Test')) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Switch
                  checked={rowData[dataKey]}
                  onCheckedChange={() => {
                    if (rowData[dataKey]) {
                      toggleModal(true, 'excludeOrgFromDeletion-table-disable', { ...rowData, excludeFromDeletion: false });
                    } else toggleModal(true, 'excludeOrgFromDeletion-table-enable', { ...rowData, excludeFromDeletion: true });
                  }}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>{textField}</TooltipContent>
          </Tooltip>
        );
      }
    } else if (['apiAccess'].includes(dataKey)) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Switch
                checked={rowData.apiAccess}
                onCheckedChange={() => {
                  if (!rowData.apiAccess) toggleModal(true, 'enableAPIAccess-table-disable', { ...rowData, apiAccess: true });
                  else toggleModal(true, 'disableAPIAccess-table-disable', { ...rowData, apiAccess: false });
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>{`${!rowData.apiAccess ? 'Enable' : 'Disable'} API Access`}</TooltipContent>
        </Tooltip>
      );
    } else {
      return (
        <div className='flex justify-center items-center'>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant='ghost' size='sm' onClick={() => history.push(`/organisations/${rowData.id}`)}>View/Edit</Button>
            </TooltipTrigger>
            <TooltipContent>View/Edit Organisation</TooltipContent>
          </Tooltip>
          {cyfAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <RefreshCw
                  size={16}
                  strokeWidth={1.5}
                  id='recalcOrgUsage'
                  className={`cursor-pointer ml-2.5 ${isWhiteLabelApp ? 'hidden' : 'block'}`}
                  onClick={() => toggleModal(true, 'orgs-table-recalcOrgUsage', rowData)}
                />
              </TooltipTrigger>
              <TooltipContent>Recalculate Usage Organisation</TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    }
  };

  if (!isArray(data)) return <Spinner />;

  const formatBillingCell = ({ rowData, dataKey }) => {
    if (dataKey === 'status') return resolveClientStatus(rowData) || (rowData.active ? 'Active' : 'Inactive');
    if (dataKey === 'billingRelationship') return billingRelationshipLabel(rowData.billingRelationship);
    return rowData[dataKey] ?? '—';
  };

  return (
    <TooltipProvider>
    <div className='mt-4 rounded-xl border border-border bg-card shadow-sm'>
      {consultantAdmin && (
        <Alert className='m-4 mb-0 border-border bg-muted/30'>
          <AlertDescription className='text-sm'>
            Add and manage client billing from{' '}
            <button type='button' className='text-primary underline' onClick={() => history.push('/consultant/portfolio')}>
              Client Portfolio
            </button>
            .
          </AlertDescription>
        </Alert>
      )}
      <header className='flex flex-wrap items-start justify-between gap-4 border-b border-border px-6 py-4'>
        <div className='flex flex-wrap items-center gap-2'>
          <Select
            id='industry-select'
            options={industryOptions}
            onChange={(selected) => setFilterIndustry(selected?.value)}
            isClearable={true}
            isDisabled={!isArray(industryOptions)}
            styles={adminPortalSelect}
            placeholder={'Industry'}
            menuPlacement='auto'
            menuPosition='fixed'
          />
          {cyfAdmin && (
            <Select
              id='industry-select'
              options={whiteLabelAdminOrgs}
              onChange={(selected) => setFilterByWhiteLabel(selected?.value)}
              isClearable={true}
              isDisabled={!isArray(whiteLabelAdminOrgs)}
              styles={adminPortalSelect}
              placeholder={'White Label'}
              menuPlacement='auto'
              menuPosition='fixed'
            />
          )}
          {cyfAdmin && (
            <Select
              id='industry-select'
              options={consultantAdminOrgs}
              onChange={(selected) => setFilterByConsultant(selected?.value)}
              isClearable={true}
              isDisabled={!isArray(consultantAdminOrgs)}
              styles={adminPortalSelect}
              placeholder={'Consultant'}
              menuPlacement='auto'
              menuPosition='fixed'
            />
          )}
          {cyfAdmin && (
            <Select
              id='industry-select'
              options={accountTypeOpt}
              onChange={(selected) => setFilterByAccountType(selected?.value)}
              isClearable={true}
              isDisabled={!isArray(accountTypeOpt)}
              styles={adminPortalSelect}
              placeholder={'Account Type'}
              menuPlacement='auto'
              menuPosition='fixed'
            />
          )}
          {cyfAdmin && (
            <Select
              id='account-level-select'
              options={accountLevelOptions}
              onChange={(selected) => setFilterByAccountLevel(selected?.value || '')}
              isClearable={true}
              styles={adminPortalSelect}
              placeholder={'Account Level'}
              menuPlacement='auto'
              menuPosition='fixed'
            />
          )}
          <Select
            id='subscription-status-select'
            options={[
              { label: 'Active', value: 'active' },
              { label: 'Grace Period', value: 'grace' }, 
              { label: 'Ended', value: 'ended' },
            ]}
            onChange={(selected) => setFilterBySubscription(selected?.value)}
            isClearable={true}
            styles={adminPortalSelect}
            placeholder={'Subscription Status'}
            menuPlacement='auto'
            menuPosition='fixed'
            sx={{ marginLeft: 5 }}
          />
          <Input
            id='outlined-basic'
            placeholder='Search'
            className='w-48 border-border'
            onChange={(e) => setFilterName(e.target.value)}
          />
        </div>
      </header>
      {isArray(data) && (
        <div className='overflow-x-auto' style={{ height: '70vh' }}>
          <div style={{ width: '100%', height: '100%', display: !data?.length > 0 ? 'none' : 'block' }}>
            <AutoSizer>
              {({ width, height }) => (
                <Table
                  width={width + 500}
                  height={height}
                  rowHeight={50}
                  headerHeight={60}
                  headerStyle={{ verticalAlign: 'bottom', width: '100%', display: 'flex', justifyContent: 'center' }}
                  rowStyle={{ textAlign: 'center' }}
                  headerClassName='admin_portal_column_headers'
                  rowCount={data?.length}
                  rowGetter={({ index }) => data[index]}>
                  {cyfAdmin && <Column label='ID' dataKey='id' width={200} cellRenderer={formatCellValue} />}
                  <Column label='Name' dataKey='name' width={300} cellRenderer={formatCellValue} />
                  {consultantAdmin && (
                    <Column label='Status' dataKey='status' width={160} cellRenderer={formatBillingCell} />
                  )}
                  {consultantAdmin && (
                    <Column
                      label='Billing'
                      dataKey='billingRelationship'
                      width={180}
                      cellRenderer={formatBillingCell}
                    />
                  )}
                  <Column
                    label='Industry'
                    dataKey='industry'
                    width={300}
                    className='cell-data'
                    cellRenderer={formatCellValue}
                  />
                  {cyfAdmin && <Column label='Country' dataKey='country' width={300} cellRenderer={formatCellValue} />}
                  {cyfAdmin && (
                    <Column label='Account Type' dataKey='accountType' width={300} cellRenderer={formatCellValue} />
                  )}
                  <Column
                    style={{ textAlign: 'center' }}
                    label='Currency'
                    dataKey='currency'
                    width={200}
                    cellRenderer={formatCellValue}
                  />
                  <Column
                    style={{ textAlign: 'center' }}
                    label='Reporting Period'
                    dataKey='reportingYearEndDay'
                    width={300}
                    cellRenderer={(reportingPeriod, formatCellValue)}
                  />
                  <Column
                    style={{ textAlign: 'center' }}
                    label='Subscription Expiry'
                    dataKey='subscriptionEndDate'
                    width={300}
                    cellRenderer={formatCellValue}
                  />
                  {cyfAdmin && (
                    <Column
                      style={{ textAlign: 'center' }}
                      label='Parent Organisation'
                      dataKey='parentOrganisation'
                      width={250}
                      cellRenderer={formatCellValue}
                    />
                  )}
                  {cyfAdmin && (
                    <Column
                      style={{ textAlign: 'center' }}
                      label='Member'
                      dataKey='userId'
                      width={300}
                      cellRenderer={formatCellValue}
                    />
                  )}
                  {cyfAdmin && (
                    <Column
                      style={{ textAlign: 'center' }}
                      label='VAT Number'
                      dataKey='vatNumber'
                      width={150}
                      cellRenderer={formatCellValue}
                    />
                  )}
                  {cyfAdmin && (
                    <Column
                      style={{ textAlign: 'center' }}
                      label='ISIN'
                      dataKey='isinNumber'
                      width={150}
                      cellRenderer={formatCellValue}
                    />
                  )}
                  {cyfAdmin && (
                    <Column
                      style={{ textAlign: 'center' }}
                      label='LEI'
                      dataKey='leiCode'
                      width={150}
                      cellRenderer={formatCellValue}
                    />
                  )}
                  {cyfAdmin && (
                    <Column
                      style={{ textAlign: 'center' }}
                      label='White Label'
                      dataKey='whiteLabelId'
                      width={350}
                      cellRenderer={formatCellValue}
                    />
                  )}
                  {cyfAdmin && (
                    <Column
                      style={{ textAlign: 'center' }}
                      label='Consultant'
                      dataKey='consultantId'
                      width={350}
                      cellRenderer={formatCellValue}
                    />
                  )}
                  <Column
                    style={{ textAlign: 'center' }}
                    label='Facility'
                    dataKey='facility'
                    width={160}
                    cellRenderer={action}
                  />
                  <Column
                    style={{ textAlign: 'center' }}
                    label='Report'
                    dataKey='report'
                    width={160}
                    cellRenderer={action}
                  />
                  <Column
                    style={{ textAlign: 'center' }}
                    label='Usage Data'
                    dataKey='usage'
                    width={160}
                    cellRenderer={action}
                  />
                  {(cyfAdmin || WhiteLabelAdmin || consultantAdmin) && (
                    <Column
                      style={{ textAlign: 'center' }}
                      label='Disable Reports'
                      dataKey='reportGeneration'
                      width={160}
                      cellRenderer={action}
                    />
                  )}
                  {(cyfAdmin || WhiteLabelAdmin || consultantAdmin) && (
                    <Column
                      style={{ textAlign: 'center' }}
                      label='Disable Add Organisation'
                      dataKey='organisationCreation'
                      width={250}
                      cellRenderer={action}
                    />
                  )}
                  {cyfAdmin && (
                    <Column
                      style={{ textAlign: 'center' }}
                      label='Exclude From Deletion'
                      dataKey='excludeFromDeletion'
                      width={300}
                      cellRenderer={action}
                    />
                  )}
                  {cyfAdmin && (
                    <Column
                      style={{ textAlign: 'center' }}
                      label='API Access'
                      dataKey='apiAccess'
                      width={160}
                      cellRenderer={action}
                    />
                  )}
                  <Column
                    style={{ alignSelf: 'center' }}
                    label='Action'
                    dataKey='action'
                    width={120}
                    flexShrink={0}
                    cellRenderer={action}
                  />
                </Table>
              )}
            </AutoSizer>
          </div>
        </div>
      )}
    </div>
    </TooltipProvider>
  );
};

const mapStateToProps = (state) => {
  const tableName = 'organisation';
  const adminOrgs = state?.admin?.organisations;
  const { id } = state.profile?.details;
  const currentOrg = state.organisation?.details;
  let sortedOrganisations;

  if (adminOrgs) {
    sortedOrganisations = isArray(adminOrgs) && _.sortBy(adminOrgs, ['id']);
  } else
    sortedOrganisations = isArray(state?.admin?.adminPortal) && _.sortBy(state?.admin?.adminPortal?.[tableName], ['id']);

  // Partner shell: only that white-label’s client orgs (not the full platform list)
  const rawList = adminOrgs || sortedOrganisations || state?.admin?.adminPortal?.[tableName];
  const scopedList =
    isWhiteLabelFirmOrg(currentOrg) && isArray(rawList)
      ? rawList.filter((x) => x.whiteLabelId === currentOrg.id && x.id !== currentOrg.id)
      : rawList;

  const statusOptions = [
    { label: 'Active', value: true },
    { label: 'Inactive', value: false },
  ];

  const isAdminOpt = [
    'Direct User',
    'Benchmark',
    'Consultant',
    'Demo',
    'Green Element Org',
    'CYF Test Organisation',
    'Client Test Organisation',
    'White Label',
  ];

  return {
    customerManagementRole: ['621810'].includes(id),
    sortedOrganisations: scopedList,
    branding: state.branding,
    industryOptions: state.organisation?.industries?.map((item) => ({
      label: item.name,
      value: item.id,
    })),
    countries: state.organisation?.countries,
    tableName,
    cyfAdmin: state.profile.details.cyfAdmin,
    WhiteLabelAdmin: state.profile.details.whiteLabelAdmin,
    consultantAdmin: state.profile.details.consultantAdmin,
    members: state?.admin?.adminPortal?.profile?.map((x) => ({
      label: `${x.firstName} ${x.lastName}`,
      value: x.id,
    })),
    statusOptions,
    isWhiteLabelApp: state.isWhiteLabelApp,
    whiteLabelAdminOrgs: state?.admin?.whiteLabelAdminOrgs?.map(itemToOptions),
    consultantAdminOrgs: sortedOrganisations
      ? sortedOrganisations?.filter((x) => x.consultantId === x.id).map(itemToOptions)
      : [],
    accountTypeOpt: isAdminOpt.map((x) => ({ label: x, value: x })),
    currencies: state?.admin?.adminPortal?.currencies,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getAllItemsFromDB: (table) => dispatch(ADMINACTIONS.getAllItemsFromDB(table)),
  setCurrentOrganisation: (organisation) => dispatch(ORGANISATIONACTIONS.setCurrentOrganisation(organisation)),
  getWLAdminOrgs: () => dispatch(ORGANISATIONACTIONS.getWLAdminOrgs()),
  toggleModal: (status, action, item, notAutoClose) =>
    dispatch(ADMINACTIONS.toggleModal(status, action, item, notAutoClose)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrganisationListTable);
